import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMsal } from "@azure/msal-react";
import { Check, Eye, Plus, Search, ShieldCheck, Trash2, UserPlus, Users } from "lucide-react";
import PortalSelect from "../components/PortalSelect";
import {
  API_BASE_URL,
  API_SCOPE,
} from "../features/portal-factory/portalFactoryConstants";

type Customer = {
  customer_id: string;
  customer_name: string;
  entity?: string | null;
  is_active?: boolean | 0 | 1;
};
type PortalUser = {
  user_object_id: string;
  user_email?: string | null;
  display_name?: string | null;
  access_role: "user" | "admin";
};
type EntraUser = {
  id: string;
  displayName?: string;
  mail?: string;
  userPrincipalName?: string;
};
type JordexPortalRole = "jordex-admin" | "jordex-user";
type JordexPortalUser = EntraUser & { role: JordexPortalRole };
type PortalUserDirectory = { customer_id: string; users?: PortalUser[] };
type PageSettings = { shipments: boolean; analytics: boolean; jpc: boolean; bookings: boolean; quotes: boolean; admin: boolean };

const customerPortal = { key: "jdx-customer-portal", label: "Customer" };
const customerRoleLabel = (role: PortalUser["access_role"]) =>
  role === "admin" ? "Customer admin" : "Customer user";
const userLabel = (user: EntraUser) =>
  user.mail || user.userPrincipalName || user.displayName || user.id;
const jordexRoleLabel = (role: JordexPortalRole) =>
  role === "jordex-admin" ? "Jordex administrator" : "Jordex user";

export default function FixedPortalPage() {
  const { instance, accounts } = useMsal();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [addingCustomer, setAddingCustomer] = useState(false);
  const [accessSearch, setAccessSearch] = useState("");
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const customerSearchRef = useRef<HTMLDivElement>(null);
  const [access, setAccess] = useState<Customer[]>([]);
  const [portalUsersByCustomer, setPortalUsersByCustomer] = useState<
    Record<string, PortalUser[]>
  >({});
  const [pageSettingsByCustomer, setPageSettingsByCustomer] = useState<Record<string, PageSettings>>({});
  const [memberDomainsByCustomer, setMemberDomainsByCustomer] = useState<Record<string, string>>({});
  const [jordexPortalUsers, setJordexPortalUsers] = useState<JordexPortalUser[]>([]);
  const [adminSearch, setAdminSearch] = useState("");
  const [adminResults, setAdminResults] = useState<EntraUser[]>([]);
  const [pendingPortalUser, setPendingPortalUser] =
    useState<EntraUser | null>(null);
  const [pendingPortalRole, setPendingPortalRole] = useState<JordexPortalRole>("jordex-user");
  const [searchingAdmins, setSearchingAdmins] = useState(false);
  const [userFormCustomerId, setUserFormCustomerId] = useState("");
  const [userEmailValue, setUserEmailValue] = useState("");
  const [userRole, setUserRole] = useState<"user" | "admin">("user");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const call = useCallback(
    async (path: string, init: RequestInit = {}) => {
      const account = accounts[0];
      if (!account) throw new Error("Sign in is required.");
      const token = (
        await instance.acquireTokenSilent({ account, scopes: [API_SCOPE] })
      ).accessToken;
      const adminPath = path.startsWith("/api/foundry/")
        ? `/api/admin${path.slice("/api".length)}`
        : path;
      const response = await fetch(`${API_BASE_URL}${adminPath}`, {
        ...init,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          ...(init.headers || {}),
        },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "Request failed.");
      return data;
    },
    [accounts, instance],
  );

  const refresh = useCallback(async () => {
    try {
      const [customerData, userDirectory, customerAccess, customerSettings, adminData] =
        await Promise.all([
          call("/api/foundry/customers"),
          call(
            `/api/foundry/portals/${customerPortal.key}/customer-users`,
          ).catch(() => ({ customers: [] })),
          call(`/api/foundry/portals/${customerPortal.key}/customers`).catch(
            () => ({ customers: [] }),
          ),
          call(`/api/foundry/portals/${customerPortal.key}/customer-settings`).catch(
            () => ({ settings: [] }),
          ),
          call("/api/admin/portal-access-users").catch(() => ({ items: [] })),
        ]);
      const rows: Customer[] = Array.isArray(customerData?.customers)
        ? customerData.customers
        : [];
      setCustomers(rows);
      setPortalUsersByCustomer(
        Object.fromEntries(
          (userDirectory?.customers || []).map((entry: PortalUserDirectory) => [
            entry.customer_id,
            entry.users || [],
          ]),
        ),
      );
      const accessibleCustomers: Customer[] = Array.isArray(customerAccess?.customers)
        ? customerAccess.customers
        : [];
      setAccess(accessibleCustomers);
      setSelectedId((id) =>
        accessibleCustomers.some((customer) => customer.customer_id === id)
          ? id
          : rows.some((customer) => customer.customer_id === id)
            ? id
            : accessibleCustomers[0]?.customer_id || "",
      );
      const settings = Array.isArray(customerSettings?.settings) ? customerSettings.settings : [];
      setPageSettingsByCustomer(Object.fromEntries(settings.map((setting: { customerId: string; pages: PageSettings }) => [setting.customerId, setting.pages])));
      setMemberDomainsByCustomer(Object.fromEntries(settings.map((setting: { customerId: string; memberDomain?: string }) => [setting.customerId, setting.memberDomain || ""])));
      setJordexPortalUsers(Array.isArray(adminData?.items) ? adminData.items : []);
    } catch (err: any) {
      setError(err.message);
    }
  }, [call]);

  useEffect(() => {
    if (accounts.length) void refresh();
  }, [accounts.length, refresh]);
  useEffect(() => {
    const query = adminSearch.trim();
    if (query.length < 2) {
      setAdminResults([]);
      setSearchingAdmins(false);
      return;
    }
    const timer = window.setTimeout(async () => {
      try {
        setSearchingAdmins(true);
        const data = await call(
          `/api/admin/entra-users/search?q=${encodeURIComponent(query)}`,
        );
        setAdminResults(Array.isArray(data?.items) ? data.items : []);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setSearchingAdmins(false);
      }
    }, 250);
    return () => window.clearTimeout(timer);
  }, [adminSearch, call]);

  const selected = useMemo(
    () => customers.find((customer) => customer.customer_id === selectedId),
    [customers, selectedId],
  );
  const matchingCustomers = useMemo(() => {
    const query = customerSearch.trim().toLocaleLowerCase();
    if (!query) return customers;
    return customers.filter((customer) =>
      [customer.customer_name, customer.customer_id, customer.entity]
        .filter(Boolean)
        .some((value) => String(value).toLocaleLowerCase().includes(query)),
    );
  }, [customerSearch, customers]);
  const visibleAccessCustomers = useMemo(() => {
    const query = accessSearch.trim().toLocaleLowerCase();
    if (!query) return access;
    return access.filter((customer) =>
      [customer.customer_name, customer.customer_id, customer.entity]
        .filter(Boolean)
        .some((value) => String(value).toLocaleLowerCase().includes(query)),
    );
  }, [access, accessSearch]);
  const selectedPortalCustomer = useMemo(
    () => access.find((customer) => customer.customer_id === selectedId),
    [access, selectedId],
  );
  const selectedAlreadyHasAccess = access.some(
    (customer) => customer.customer_id === selectedId,
  );
  useEffect(() => {
    if (!customerSearchOpen) setCustomerSearch(selected?.customer_name || "");
  }, [customerSearchOpen, selected?.customer_id, selected?.customer_name]);
  useEffect(() => {
    if (!customerSearchOpen) return;
    const close = (event: MouseEvent) => {
      if (!customerSearchRef.current?.contains(event.target as Node))
        setCustomerSearchOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [customerSearchOpen]);

  const run = async (key: string, action: () => Promise<void>) => {
    setBusy(key);
    setError("");
    setNotice("");
    try {
      await action();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy("");
    }
  };
  const addCustomerToPortal = () =>
    run("portal", async () => {
      if (!selected) return;
      await call(`/api/foundry/portals/${customerPortal.key}/customers`, {
        method: "POST",
        body: JSON.stringify({
          selectedCustomer: {
            customerId: selected.customer_id,
            customerName: selected.customer_name,
            entity: selected.entity,
          },
        }),
      });
      setNotice(
        `${selected.customer_name} now has ${customerPortal.label} access.`,
      );
      await refresh();
      setAddingCustomer(false);
    });
  const addPortalUser = (customer: Customer) =>
    run(`portal-user-${customer.customer_id}`, async () => {
      if (!userEmailValue.trim()) return;
      const result = await call(
        `/api/foundry/portals/${customerPortal.key}/customers/${encodeURIComponent(customer.customer_id)}/users`,
        {
          method: "POST",
          body: JSON.stringify({
            email: userEmailValue.trim(),
            role: userRole,
          }),
        },
      );
      setNotice(
        result?.invited
          ? `${userEmailValue.trim()} was invited and assigned immediately.`
          : "User access was assigned.",
      );
      setUserEmailValue("");
      setUserFormCustomerId("");
      await refresh();
    });
  const removePortalUser = (customerId: string, user: PortalUser) =>
    run(`remove-${user.user_object_id}`, async () => {
      await call(
        `/api/foundry/portals/${customerPortal.key}/customers/${encodeURIComponent(customerId)}/users/${encodeURIComponent(user.user_object_id)}`,
        { method: "DELETE" },
      );
      setNotice("User access was removed.");
      await refresh();
    });
  const savePageSettings = (customer: Customer, pages: PageSettings) =>
    run(`pages-${customer.customer_id}`, async () => {
      const result = await call(`/api/foundry/portals/${customerPortal.key}/customers/${encodeURIComponent(customer.customer_id)}/page-settings`, { method: "PUT", body: JSON.stringify({ pages }) });
      const confirmed = result?.pages || pages;
      setPageSettingsByCustomer((current) => ({ ...current, [customer.customer_id]: confirmed }));
      setNotice(`${customer.customer_name}: page access settings saved.`);
    });
  const togglePageSetting = (
    customer: Customer,
    currentPages: PageSettings,
    page: keyof PageSettings,
  ) => {
    const pages = { ...currentPages, [page]: !currentPages[page] };
    setPageSettingsByCustomer((current) => ({
      ...current,
      [customer.customer_id]: pages,
    }));
    void savePageSettings(customer, pages);
  };
  const saveMemberDomain = (customer: Customer, domain: string) =>
    run(`member-domain-${customer.customer_id}`, async () => {
      const normalizedDomain = domain.trim().replace(/^@?/, "@").toLowerCase();
      if (normalizedDomain === "@") return;
      const result = await call(`/api/foundry/portals/${customerPortal.key}/customers/${encodeURIComponent(customer.customer_id)}/member-domain`, { method: "PUT", body: JSON.stringify({ domain: normalizedDomain }) });
      setMemberDomainsByCustomer((current) => ({ ...current, [customer.customer_id]: result.domain }));
      setNotice(`${customer.customer_name}: member domain changed to ${result.domain}.`);
    });
  const queueCustomerLoad = (
    customer: Customer,
    action: "preview" | "refresh",
  ) =>
    run(`load-${customer.customer_id}-${action}`, async () => {
      const result = await call(
        `/api/foundry/portals/${customerPortal.key}/customers/${encodeURIComponent(customer.customer_id)}/load`,
        { method: "POST", body: JSON.stringify({ action }) },
      );
      setNotice(
        `${result.customerName || customer.customer_name}: ${action === "preview" ? "preview load" : "production refresh"} queued.`,
      );
    });
  const assignJordexPortalRole = (user: EntraUser, role: JordexPortalRole) =>
    run(`assign-${role}-${user.id}`, async () => {
      await call("/api/admin/portal-access-users", {
        method: "POST",
        body: JSON.stringify({ userId: user.id, role }),
      });
      setNotice(`${userLabel(user)} is now a ${jordexRoleLabel(role)}.`);
      setAdminSearch("");
      setAdminResults([]);
      setPendingPortalUser(null);
      await refresh();
    });
  const removeJordexPortalUser = (user: JordexPortalUser) =>
    run(`remove-${user.role}-${user.id}`, async () => {
      await call(`/api/admin/portal-access-users/${encodeURIComponent(user.id)}/${user.role}`, {
        method: "DELETE",
      });
      setNotice(`${jordexRoleLabel(user.role)} access was removed.`);
      await refresh();
    });
  const setCustomerPortalActive = (customer: Customer, isActive: boolean) =>
    run(`portal-active-${customer.customer_id}`, async () => {
      if (!window.confirm(`${isActive ? "Activate" : "Deactivate"} ${customer.customer_name}'s customer portal access?${isActive ? "" : " It will immediately be unavailable to customer and Jordex users."}`)) return;
      await call(`/api/foundry/portals/${customerPortal.key}/customers/${encodeURIComponent(customer.customer_id)}/active`, {
        method: "PUT",
        body: JSON.stringify({ isActive }),
      });
      setNotice(`${customer.customer_name}'s customer portal is now ${isActive ? "active" : "inactive"}.`);
      await refresh();
    });

  return (
    <main className="min-h-screen w-full bg-white text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <section className="mx-auto w-full max-w-[1600px]">
        <header className="mb-5 overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900/50">
          <div className="border-l-4 border-orange-500 px-5 py-5">
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-orange-500">Customer portal</p>
            <h1 className="mt-1 text-xl font-semibold">Customer access</h1>
            <p className="mt-1 max-w-2xl text-sm text-slate-500">Add customers, choose which portal pages they can use, and manage their members in one place.</p>
          </div>
        </header>
        {error && (
          <div className="border-l-2 border-red-400 bg-red-500/10 px-4 py-3 text-sm text-red-200">
            {error}
          </div>
        )}
        {notice && (
          <div className="border-l-2 border-emerald-400 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200">
            {notice}
          </div>
        )}
        <div className="hidden" aria-hidden="true">
          <div className="min-w-[260px] flex-1">
            <label
              htmlFor="customer-search-template"
              className="text-[11px] font-bold uppercase tracking-wide text-slate-500"
            >
              Add customer to portal
            </label>
            <div ref={customerSearchRef} className="relative mt-2">
              <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
              <input
                id="customer-search-template"
                value={customerSearch}
                onFocus={(event) => {
                  event.currentTarget.select();
                  setCustomerSearchOpen(true);
                }}
                onChange={(event) => {
                  setCustomerSearch(event.target.value);
                  setCustomerSearchOpen(true);
                }}
                className="w-full rounded-md border border-white/10 bg-[#0b1120] py-2 pl-9 pr-3 text-sm outline-none placeholder:text-slate-600 focus:border-orange-400"
                placeholder="Search customer name or ID"
              />
              {customerSearchOpen && (
                <ul className="absolute z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-white/10 bg-[#111a2d] shadow-xl">
                  {matchingCustomers.length ? (
                    matchingCustomers.map((customer) => (
                      <li key={customer.customer_id}>
                        <button
                          type="button"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => {
                            setSelectedId(customer.customer_id);
                            setCustomerSearch(customer.customer_name);
                            setCustomerSearchOpen(false);
                          }}
                          className={`w-full px-3 py-2 text-left hover:bg-white/[0.05] ${customer.customer_id === selectedId ? "bg-orange-500/10" : ""}`}
                        >
                          <p className="truncate text-sm font-medium">
                            {customer.customer_name}
                          </p>
                          <p className="mt-0.5 truncate font-mono text-[11px] text-slate-500">
                            {customer.customer_id}
                            {customer.entity ? ` · ${customer.entity}` : ""}
                          </p>
                        </button>
                      </li>
                    ))
                  ) : (
                    <li className="px-3 py-3 text-xs text-slate-500">
                      No customers found.
                    </li>
                  )}
                </ul>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={addCustomerToPortal}
            disabled={!selected || !!busy}
            className="flex h-9 items-center gap-2 rounded-md bg-orange-500 px-3 text-sm font-semibold text-slate-950 hover:bg-orange-400 disabled:opacity-40"
          >
            <Plus className="h-4 w-4" />
            Add customer
          </button>
        </div>
        <div className="grid gap-5 xl:grid-cols-[260px_minmax(0,1fr)_320px] xl:items-start">
          <aside className="overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900/50">
            <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-800">
              <div className="flex items-center justify-between gap-2"><div className="flex items-center gap-2"><Users className="h-4 w-4 text-orange-500" /><h2 className="text-sm font-semibold">Portal customers</h2></div><span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500 dark:bg-white/10 dark:text-slate-300">{access.length}</span></div>
              <div className="relative mt-3"><Search className="pointer-events-none absolute left-2.5 top-2 h-3.5 w-3.5 text-slate-500" /><input value={accessSearch} onChange={(event) => setAccessSearch(event.target.value)} className="h-8 w-full rounded border border-slate-200 bg-slate-50 py-1 pl-8 pr-2 text-xs outline-none placeholder:text-slate-500 focus:border-orange-400 dark:border-slate-800 dark:bg-slate-950 dark:placeholder:text-slate-600" placeholder="Filter customers" /></div>
            </div>
            <nav aria-label="Portal customers" className="max-h-[calc(100vh-270px)] overflow-y-auto p-2">
              {visibleAccessCustomers.length ? visibleAccessCustomers.map((customer) => {
                const isSelected = customer.customer_id === selectedId;
                const active = customer.is_active !== false && customer.is_active !== 0;
                return <button key={customer.customer_id} type="button" onClick={() => setSelectedId(customer.customer_id)} className={`mb-1 w-full rounded-md border px-3 py-2 text-left transition last:mb-0 ${isSelected ? "border-orange-400 bg-orange-500/10" : "border-transparent hover:border-slate-200 hover:bg-slate-50 dark:hover:border-slate-800 dark:hover:bg-white/[0.04]"}`}><div className="flex items-center justify-between gap-2"><span className="truncate text-xs font-semibold">{customer.customer_name}</span><span className={`h-1.5 w-1.5 shrink-0 rounded-full ${active ? "bg-emerald-400" : "bg-red-400"}`} title={active ? "Portal active" : "Portal inactive"} /></div></button>;
              }) : <p className="px-2 py-5 text-center text-xs text-slate-500">No portal customers found.</p>}
            </nav>
          </aside>
          <article className="overflow-hidden rounded-lg border border-slate-200 bg-[rgb(255_255_255)] dark:border-slate-800 dark:bg-slate-900/50">
            <div className="relative z-30 border-b border-slate-200 px-5 py-4 dark:border-slate-800">
              <div className="flex items-center justify-between gap-3"><div className="flex items-center gap-2"><Users className="h-4 w-4 text-orange-500" /><h2 className="font-semibold">Customer settings</h2></div>{!addingCustomer && <button type="button" onClick={() => { setAddingCustomer(true); setCustomerSearch(""); setCustomerSearchOpen(true); }} disabled={!!busy} className="flex h-8 items-center gap-1.5 rounded bg-orange-500 px-2.5 text-xs font-semibold text-slate-950 hover:bg-orange-400 disabled:opacity-40"><Plus className="h-3.5 w-3.5" />Add customer</button>}</div>
              {addingCustomer && <div className="mt-3 flex flex-wrap items-end gap-3">
                <div className="min-w-[260px] flex-1">
                  <label
                    htmlFor="customer-search"
                    className="text-[11px] font-bold uppercase tracking-wide text-slate-500"
                  >
                    Add customer
                  </label>
                  <div ref={customerSearchRef} className="relative mt-2">
                    <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
                    <input
                      id="customer-search"
                      value={customerSearch}
                      onFocus={(event) => {
                        event.currentTarget.select();
                        setCustomerSearchOpen(true);
                      }}
                      onChange={(event) => {
                        setCustomerSearch(event.target.value);
                        setCustomerSearchOpen(true);
                      }}
                      className="w-full rounded-md border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm outline-none placeholder:text-slate-500 focus:border-orange-400 dark:border-slate-800 dark:bg-slate-950 dark:placeholder:text-slate-600"
                      placeholder="Search customer name or ID"
                    />
                    {customerSearchOpen && (
                      <ul className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-950">
                        {matchingCustomers.length ? (
                          matchingCustomers.map((customer) => (
                            <li key={customer.customer_id}>
                              <button
                                type="button"
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => {
                                  setSelectedId(customer.customer_id);
                                  setCustomerSearch(customer.customer_name);
                                  setCustomerSearchOpen(false);
                                }}
                                className={`w-full px-3 py-2 text-left hover:bg-slate-100 dark:hover:bg-white/[0.05] ${customer.customer_id === selectedId ? "bg-orange-500/10" : ""}`}
                              >
                                <p className="truncate text-sm font-medium">
                                  {customer.customer_name}
                                </p>
                                <p className="mt-0.5 truncate font-mono text-[11px] text-slate-500">
                                  {customer.customer_id}
                                  {customer.entity
                                    ? ` · ${customer.entity}`
                                    : ""}
                                </p>
                              </button>
                            </li>
                          ))
                        ) : (
                          <li className="px-3 py-3 text-xs text-slate-500">
                            No customers found.
                          </li>
                        )}
                      </ul>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={addCustomerToPortal}
                  disabled={!selected || selectedAlreadyHasAccess || !!busy}
                  className="flex h-9 items-center gap-2 rounded-md bg-orange-500 px-3 text-sm font-semibold text-slate-950 hover:bg-orange-400 disabled:opacity-40"
                >
                  <Plus className="h-4 w-4" />
                  {selectedAlreadyHasAccess ? "Already added" : "Add"}
                </button>
                <button type="button" onClick={() => { setAddingCustomer(false); setCustomerSearchOpen(false); }} disabled={!!busy} className="h-9 rounded-md border border-slate-200 px-3 text-sm font-semibold text-slate-600 hover:text-slate-950 disabled:opacity-40 dark:border-slate-800 dark:text-slate-300 dark:hover:text-white">Cancel</button>
              </div>}
            </div>
            <ul>
              {selectedPortalCustomer ? (
                [selectedPortalCustomer].map((customer) => {
                  const customerUsers =
                    portalUsersByCustomer[customer.customer_id] || [];
                  const pageSettings = pageSettingsByCustomer[customer.customer_id] || { shipments: true, analytics: true, jpc: false, bookings: false, quotes: false, admin: false };
                  const memberDomain = memberDomainsByCustomer[customer.customer_id] || "";
                  const previewBusy =
                    busy === `load-${customer.customer_id}-preview`;
                  const refreshBusy =
                    busy === `load-${customer.customer_id}-refresh`;
                  const addingUser =
                    userFormCustomerId === customer.customer_id;
                  const customerAdminCount = customerUsers.filter((user) => user.access_role === "admin").length;
                  const portalIsActive = customer.is_active !== false && customer.is_active !== 0;
                  return (
                    <li
                      key={customer.customer_id}
                      className="overflow-hidden dark:bg-slate-950/40"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3 bg-slate-50 px-5 py-4 dark:bg-white/[0.03]">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2"><h3 className="truncate text-base font-semibold">
                            {customer.customer_name}
                          </h3></div>
                          <p className="mt-1 truncate font-mono text-[11px] text-slate-500">
                            {customer.customer_id}
                          </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => setCustomerPortalActive(customer, !portalIsActive)}
                            disabled={!!busy}
                            className={`rounded border px-2 py-1 text-[11px] font-semibold disabled:opacity-40 ${portalIsActive ? "border-red-300 text-red-700 hover:bg-red-50 dark:border-red-500/30 dark:text-red-300 dark:hover:bg-red-500/10" : "border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-500/30 dark:text-emerald-300 dark:hover:bg-emerald-500/10"}`}
                          >
                            {busy === `portal-active-${customer.customer_id}` ? "Saving..." : portalIsActive ? "Deactivate portal" : "Activate portal"}
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              queueCustomerLoad(customer, "preview")
                            }
                            disabled={!!busy}
                            className="rounded border border-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-700 hover:border-sky-400/60 hover:text-slate-950 disabled:opacity-40 dark:border-slate-800 dark:text-slate-300 dark:hover:text-white"
                          >
                            <Eye className="mr-1 inline h-3.5 w-3.5" />{previewBusy ? "Queueing..." : "Load preview"}
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              queueCustomerLoad(customer, "refresh")
                            }
                            disabled={!!busy}
                            className="rounded bg-orange-500 px-2 py-1 text-[11px] font-semibold text-slate-950 hover:bg-orange-400 disabled:opacity-40"
                          >
                            {refreshBusy ? "Queueing..." : "Load data"}
                          </button>
                        </div>
                      </div>
                      <div className="px-5 py-4">
                        <div><p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">1. Available pages</p><p className="mt-0.5 text-[11px] text-slate-500">Changes save immediately for everyone, including Jordex admins.</p></div>
                        <div className="mt-3 flex flex-wrap gap-1.5">{(["shipments", "analytics", "jpc", "bookings", "quotes", "admin"] as const).map((page) => <button key={page} type="button" role="switch" aria-checked={pageSettings[page]} onClick={() => togglePageSetting(customer, pageSettings, page)} disabled={!!busy} className={`flex h-8 items-center gap-1 rounded border px-2 text-[11px] font-semibold capitalize transition-colors disabled:opacity-40 ${pageSettings[page] ? "border-emerald-400/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" : "border-slate-200 bg-slate-50 text-slate-500 dark:border-slate-800 dark:bg-slate-900"}`}><Check className={`h-3 w-3 ${pageSettings[page] ? "opacity-100" : "opacity-0"}`} />{page}</button>)}</div>
                      </div>
                      <div className="border-t border-slate-200 px-5 py-4 dark:border-slate-800">
                        <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">2. Member invitation domain</p><p className="mt-0.5 text-[11px] text-slate-500">New members must use this email domain. Changes save when you leave the field.</p>
                        <div className="mt-3"><input id={`member-domain-${customer.customer_id}`} value={memberDomain} onChange={(event) => setMemberDomainsByCustomer((current) => ({ ...current, [customer.customer_id]: event.target.value }))} onBlur={() => { if (memberDomain.trim()) void saveMemberDomain(customer, memberDomain); }} placeholder="@company.com" disabled={!!busy} className="h-8 w-44 rounded border border-slate-200 bg-slate-50 px-2 text-xs outline-none placeholder:text-slate-500 focus:border-orange-400 disabled:opacity-40 dark:border-slate-800 dark:bg-slate-950 dark:placeholder:text-slate-600" /></div>
                      </div>
                      <div className="border-t border-slate-200 px-5 py-4 dark:border-slate-800"><div className="flex items-start justify-between gap-3"><div><p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">3. Members</p><p className="mt-0.5 text-[11px] text-slate-500">{customerUsers.length ? `${customerUsers.length} member${customerUsers.length === 1 ? "" : "s"} · ${customerAdminCount} customer admin${customerAdminCount === 1 ? "" : "s"}` : "No members have been added yet."}</p></div><button type="button" onClick={() => { setUserFormCustomerId(addingUser ? "" : customer.customer_id); setUserEmailValue(""); }} disabled={!!busy} className="flex h-8 items-center gap-1 rounded border border-slate-200 px-2 text-[11px] font-semibold text-slate-700 hover:border-emerald-400/60 hover:text-slate-950 disabled:opacity-40 dark:border-slate-800 dark:text-slate-300 dark:hover:text-white"><UserPlus className="h-3.5 w-3.5" />{addingUser ? "Close" : "Add member"}</button></div>
                      {addingUser && <form onSubmit={(event) => { event.preventDefault(); void addPortalUser(customer); }} className="mt-3 grid gap-2 border-t border-slate-200 pt-3 sm:grid-cols-[minmax(0,1.4fr)_auto_auto] dark:border-slate-800"><input value={userEmailValue} onChange={(event) => setUserEmailValue(event.target.value)} className="min-w-0 rounded border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs outline-none placeholder:text-slate-500 focus:border-orange-400 dark:border-slate-800 dark:bg-slate-950 dark:placeholder:text-slate-600" placeholder="External email address" autoFocus /><select value={userRole} onChange={(event) => setUserRole(event.target.value as "user" | "admin")} className="rounded border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs outline-none focus:border-orange-400 dark:border-slate-800 dark:bg-slate-950"><option value="user">Customer user</option><option value="admin">Customer admin</option></select><button type="submit" disabled={!!busy || !userEmailValue.trim()} className="rounded bg-orange-500 px-2.5 py-1.5 text-xs font-semibold text-slate-950 hover:bg-orange-400 disabled:opacity-40">{busy === `portal-user-${customer.customer_id}` ? "Adding..." : "Add"}</button></form>}
                      {customerUsers.length ? (
                        <ul className="mt-3 space-y-1.5">
                          {customerUsers.map((user) => (
                            <li
                              key={user.user_object_id}
                              className="flex items-center gap-2 text-xs text-slate-400"
                            >
                              <span className="truncate">{user.user_email || user.user_object_id}</span>
                              <span className="rounded-full bg-white/5 px-2 py-0.5 text-[10px] font-medium text-slate-300">
                                {customerRoleLabel(user.access_role)}
                              </span>
                              <button
                                type="button"
                                onClick={() =>
                                  removePortalUser(customer.customer_id, user)
                                }
                                disabled={!!busy}
                                title="Remove access"
                                className="rounded p-1 text-slate-500 hover:bg-red-500/10 hover:text-red-300 disabled:opacity-40"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        null
                      )}
                      </div>
                    </li>
                  );
                })
              ) : (
                <li className="px-5 py-12 text-center text-sm text-slate-500">
                  Select a portal customer from the list, or search above to add one.
                </li>
              )}
            </ul>
          </article>
          <article className="overflow-hidden rounded-lg border border-slate-200 bg-[rgb(255_255_255)] dark:border-slate-800 dark:bg-slate-900/50">
            <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-800">
              <div className="flex items-center justify-between gap-2"><div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-orange-500" /><h2 className="text-sm font-semibold">Jordex access</h2></div><span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500 dark:bg-white/10 dark:text-slate-300">{jordexPortalUsers.length}</span></div>
              <p className="mt-1 text-[11px] text-slate-500">Manage administrators and users across portal customers.</p>
              <div className="relative mt-2">
                <Search className="pointer-events-none absolute left-2.5 top-2 h-3.5 w-3.5 text-slate-500" />
                <input
                  value={adminSearch}
                  onChange={(event) => {
                    setAdminSearch(event.target.value);
                    setPendingPortalUser(null);
                  }}
                  className="h-8 w-full rounded border border-slate-200 bg-slate-50 py-1 pl-8 pr-2 text-xs outline-none placeholder:text-slate-500 focus:border-orange-400 dark:border-slate-800 dark:bg-slate-950 dark:placeholder:text-slate-600"
                  placeholder="Search Entra user"
                />
              </div>
              {searchingAdmins && (
                <p className="mt-2 text-xs text-slate-500">Searching...</p>
              )}
              {adminResults.length > 0 && (
                <ul className="mt-2 overflow-hidden rounded-md border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950">
                  {adminResults.map((user) => (
                    <li key={user.id}>
                      <button
                        type="button"
                        onClick={() => setPendingPortalUser(user)}
                        className={`w-full border-b border-slate-200 px-3 py-2 text-left last:border-0 dark:border-slate-800 ${pendingPortalUser?.id === user.id ? "bg-orange-500/10" : "hover:bg-slate-100 dark:hover:bg-white/[0.04]"}`}
                      >
                        <p className="truncate text-xs font-medium">
                          {userLabel(user)}
                        </p>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {pendingPortalUser && (
                <div className="mt-2 rounded border border-orange-400/30 bg-orange-500/10 px-2.5 py-2">
                  <p className="truncate text-[11px] text-slate-200">Assign access to {userLabel(pendingPortalUser)}</p>
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    <div className="h-8 w-44 rounded border border-slate-700 bg-slate-950 px-2"><PortalSelect value={pendingPortalRole} onChange={(value) => setPendingPortalRole(value as JordexPortalRole)} ariaLabel="Portal role" options={[{ value: "jordex-user", label: "Jordex user" }, { value: "jordex-admin", label: "Jordex administrator" }]} /></div>
                    <button
                      type="button"
                      onClick={() => assignJordexPortalRole(pendingPortalUser, pendingPortalRole)}
                      disabled={!!busy}
                      className="rounded bg-orange-500 px-2.5 py-1.5 text-xs font-semibold text-slate-950 hover:bg-orange-400 disabled:opacity-40"
                    >
                      {busy === `assign-${pendingPortalRole}-${pendingPortalUser.id}` ? "Adding..." : "Add"}
                    </button>
                  </div>
                </div>
              )}
            </div>
            <ul>
              {jordexPortalUsers.length ? (
                jordexPortalUsers.map((user) => (
                  <li
                    key={user.id}
                    className="flex items-center justify-between gap-2 border-b border-slate-200 px-3 py-2 last:border-0 dark:border-slate-800"
                  >
                    <div className="min-w-0">
                      <div className="flex min-w-0 items-center gap-1.5"><p className="truncate text-xs font-medium">{userLabel(user)}</p><span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold ${user.role === "jordex-admin" ? "bg-orange-500/10 text-orange-600 dark:text-orange-300" : "bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-slate-300"}`}>{user.role === "jordex-admin" ? "Admin" : "User"}</span></div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeJordexPortalUser(user)}
                      disabled={!!busy}
                      title={`Remove ${jordexRoleLabel(user.role)}`}
                      className="rounded p-1 text-slate-500 hover:bg-red-500/10 hover:text-red-300 disabled:opacity-40"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))
              ) : (
                <li className="px-5 py-6 text-sm text-slate-500">
                  No Jordex administrators or users assigned.
                </li>
              )}
            </ul>
          </article>
        </div>
      </section>
    </main>
  );
}
